import StudioApi, {
  AddSceneBody,
  EndingBody,
  PublishBody,
  SceneEdit,
  StudioApiResponse,
  StudioFailureDetail,
  StudioRequestOptions,
  StudioVersion,
} from '../api/StudioApi';
import {ApiResponse} from '../models/ApiResponse';
import type {
  BrandColorRole,
  ReelMotionUpdate,
} from '../models/Studio/StudioModels';
import {
  ApiError,
  CREDIT_CHECK_PENDING,
  CREDIT_CHECK_PENDING_CODE,
  GENERIC_ERROR,
  INSUFFICIENT_CREDITS,
  safeMessage,
} from '../models/Studio/StudioError';
import {studioErrorText} from '../models/Studio/studioErrorText';

/**
 * A Studio request's result. A failure keeps its HTTP status, and `error` (whose message is
 * also in `errors`) says why in words for authors.
 */
export interface StudioResponse<T> extends ApiResponse<T> {
  status?: number;
  error?: ApiError;
}

/** How the Studio server words a failure it does not explain. */
const SERVER_GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * The error for a failed Studio request. Only Studio's own `{error}` answer to a request it
 * refused (4xx) is written for authors, and keeps the `code` that names the refusal (e.g.
 * insufficient_credits); the refusals it names are said in the author's language. Server
 * failures, proxies and the platform's exception handler (plain strings) can carry technical
 * detail. `catalog` marks the /options request, whose 404 or 503 means Studio is missing from
 * this environment. `creditCheckPending` is what a refusal for a pending credit check says.
 */
export function studioFailure(
  status: number,
  detail: StudioFailureDetail | undefined,
  catalog = false,
  creditCheckPending = CREDIT_CHECK_PENDING,
): ApiError {
  const answer = (detail && typeof detail === 'object' ? detail : {}) as {
    error?: unknown;
    code?: unknown;
    unavailable?: unknown;
    reconciliationRequired?: unknown;
  };
  const message =
    typeof answer.error === 'string' && answer.error.trim()
      ? answer.error
      : null;
  const fromServer = message !== null;
  // A pending credit check refuses AI actions with the generic message; say what it is.
  if (
    answer.reconciliationRequired === true &&
    (message === null || message === SERVER_GENERIC_ERROR)
  )
    return new ApiError(
      creditCheckPending,
      status,
      fromServer,
      CREDIT_CHECK_PENDING_CODE,
    );
  // 404: this API has no Studio routes; 503: it has them but Studio is not configured here.
  if (
    ((status === 404 || status === 503) && catalog) ||
    (status === 503 && answer.unavailable === true)
  )
    return new ApiError(studioErrorText.studioUnavailable, status, fromServer);
  if (message !== null && status >= 400 && status < 500) {
    const code =
      typeof answer.code === 'string' && answer.code ? answer.code : undefined;
    // The server words the refusals it names in English.
    const text =
      code === INSUFFICIENT_CREDITS
        ? studioErrorText.insufficientCredits
        : code === CREDIT_CHECK_PENDING_CODE
        ? creditCheckPending
        : message === SERVER_GENERIC_ERROR
        ? GENERIC_ERROR
        : safeMessage(message);
    return new ApiError(text, status, true, code);
  }
  if (status === 409)
    return new ApiError(studioErrorText.storyboardConflict, status);
  // Studio's rate limit answers with a bare 429: say to wait rather than to try again now.
  if (status === 429 && message === null)
    return new ApiError(studioErrorText.tooManyRequests, status);
  return new ApiError(GENERIC_ERROR, status, fromServer);
}

class StudioRepository {
  private studioApi = new StudioApi();

  public async sceneImage(
    organizationId: string,
    id: string,
    kind: 'images' | 'stock',
    asset: string,
    options?: StudioRequestOptions,
  ) {
    return this.result(
      this.studioApi.sceneImage(organizationId, id, kind, asset, options),
    );
  }

  public async options<T = Record<string, unknown>>(organizationId: string) {
    return this.result(this.studioApi.options<T>(organizationId), true);
  }

  public async voices(
    organizationId: string,
    query: {language: string; region: string},
    options?: StudioRequestOptions,
  ) {
    return this.result(this.studioApi.voices(organizationId, query, options));
  }

  public async capacity(
    organizationId: string,
    options?: StudioRequestOptions,
  ) {
    return this.result(this.studioApi.capacity(organizationId, options));
  }

  public async listJobs(
    organizationId: string,
    options?: StudioRequestOptions,
  ) {
    return this.result(this.studioApi.listJobs(organizationId, options));
  }

  public async getJob(
    organizationId: string,
    id: string,
    options?: StudioRequestOptions,
  ) {
    return this.result(this.studioApi.getJob(organizationId, id, options));
  }

  public async createJob(
    organizationId: string,
    brief: unknown,
    file?: File | null,
    documentText?: string,
  ) {
    return this.result(
      this.studioApi.createJob(organizationId, brief, file, documentText),
    );
  }

  public async renameJob(
    organizationId: string,
    id: string,
    title: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.renameJob(organizationId, id, title, version),
    );
  }

  public async renameReel(
    organizationId: string,
    id: string,
    reelId: string,
    title: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.renameReel(organizationId, id, reelId, title, version),
    );
  }

  public async deleteJob(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.deleteJob(organizationId, id, version),
      false,
      studioErrorText.deleteCreditCheckPending,
    );
  }

  public async replan(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.result(this.studioApi.replan(organizationId, id, version));
  }

  public async chooseTopics(
    organizationId: string,
    id: string,
    topics: string[],
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.chooseTopics(organizationId, id, topics, version),
    );
  }

  public async addScene(
    organizationId: string,
    id: string,
    reelId: string,
    scene: AddSceneBody,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.addScene(organizationId, id, reelId, scene, version),
    );
  }

  public async editScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    edit: SceneEdit,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.editScene(
        organizationId,
        id,
        reelId,
        sceneId,
        edit,
        version,
      ),
    );
  }

  public async deleteScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.deleteScene(organizationId, id, reelId, sceneId, version),
    );
  }

  public async moveScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    direction: number,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.moveScene(
        organizationId,
        id,
        reelId,
        sceneId,
        direction,
        version,
      ),
    );
  }

  public async rewriteScene(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    instruction: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.rewriteScene(
        organizationId,
        id,
        reelId,
        sceneId,
        instruction,
        version,
      ),
    );
  }

  public async approveOverride(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.approveOverride(
        organizationId,
        id,
        reelId,
        sceneId,
        version,
      ),
    );
  }

  public async resolveSme(
    organizationId: string,
    id: string,
    reelId: string,
    response: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.resolveSme(organizationId, id, reelId, response, version),
    );
  }

  public async suggestQuestion(
    organizationId: string,
    id: string,
    reelId: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.suggestQuestion(organizationId, id, reelId, version),
    );
  }

  public async setEnding(
    organizationId: string,
    id: string,
    reelId: string,
    ending: EndingBody,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.setEnding(organizationId, id, reelId, ending, version),
    );
  }

  public async tryAnotherLook(
    organizationId: string,
    id: string,
    reelId: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.tryAnotherLook(organizationId, id, reelId, version),
    );
  }

  public async tryAnotherAnimation(
    organizationId: string,
    id: string,
    reelId: string,
    sceneId: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.tryAnotherAnimation(
        organizationId,
        id,
        reelId,
        sceneId,
        version,
      ),
    );
  }

  public async updateReelMotion(
    organizationId: string,
    id: string,
    reelId: string,
    change: ReelMotionUpdate,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.updateReelMotion(
        organizationId,
        id,
        reelId,
        change,
        version,
      ),
    );
  }

  public async updateBrandColors(
    organizationId: string,
    id: string,
    colors: Partial<Record<BrandColorRole, string>>,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.updateBrandColors(organizationId, id, colors, version),
    );
  }

  public async updateBrandFont(
    organizationId: string,
    id: string,
    fontFamily: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.updateBrandFont(organizationId, id, fontFamily, version),
    );
  }

  public async uploadBrandLogo(
    organizationId: string,
    id: string,
    logo: Blob,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.uploadBrandLogo(organizationId, id, logo, version),
    );
  }

  public async removeBrandLogo(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.result(
      this.studioApi.removeBrandLogo(organizationId, id, version),
    );
  }

  public async render(
    organizationId: string,
    id: string,
    version: StudioVersion,
    versionHash?: string,
  ) {
    return this.result(
      this.studioApi.render(organizationId, id, version, versionHash),
    );
  }

  public async cancel(
    organizationId: string,
    id: string,
    version: StudioVersion,
  ) {
    return this.result(this.studioApi.cancel(organizationId, id, version));
  }

  /** Settles the AI credits a stopped generation holds; resolves to whether they settled. */
  public async creditCheck(organizationId: string, id: string) {
    return this.result(this.studioApi.creditCheck(organizationId, id));
  }

  public async publication(organizationId: string, id: string) {
    return this.result(this.studioApi.publication(organizationId, id));
  }

  public async publish(
    organizationId: string,
    id: string,
    body: PublishBody = {},
  ) {
    return this.result(this.studioApi.publish(organizationId, id, body));
  }

  /** A failure says why for authors; see `studioFailure`. */
  private async result<T>(
    request: Promise<StudioApiResponse<T>>,
    catalog = false,
    creditCheckPending?: string,
  ): Promise<StudioResponse<T>> {
    const response = await request;
    if (response?.success !== false)
      return {success: true, data: response?.data};
    // The client puts the response body, or the platform's Errors list, in `errors`.
    const detail = (response.errors || []).find(
      value => value && value !== 'unknown' && value !== 'timeout',
    );
    const error = studioFailure(
      response.status || 0,
      detail,
      catalog,
      creditCheckPending,
    );
    return {
      success: false,
      status: error.status,
      errors: [error.message],
      error,
    };
  }
}

export default StudioRepository;
